import React, { SFC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'
import { Charger, getLabelForChargerConnectorSocketType, getLabelForChargerConnectorType } from '../../../shared/types/charger'
import { ChargingSession, PaymentStatus } from '../../../shared/types/chargingSession'
import { getJson } from '../../../shared/utils/api'
import SessionCard from '../../history/SessionCard'
import { NoData } from '../../../shared/components/NoData'

const PAGE_SIZE = 30

type Option = { id: any; label: string }

const useStyles = makeStyles((theme) => ({
    filters: {
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(1),
    },
    filter: {
        flex: '1 1 260px',
        backgroundColor: '#f5f5f5',
        borderRadius: theme.shape.borderRadius,
        border: '1px solid #ddd',
        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5',
            borderRadius: theme.shape.borderRadius,
            paddingBottom: '6px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none',
            },
        },
        '& .MuiInputLabel-root': {
            fontSize: '0.9rem',
            color: '#9e9e9e',
        },
        '& .MuiChip-root': {
            fontSize: '0.7rem',
            height: '2em',
        },
    },
    content: {
        minHeight: '60vh',
    },
    dayTitle: {
        marginLeft: theme.spacing(1),
        marginTop: theme.spacing(2),
        textAlign: 'left',
    },
    footer: {
        display: 'flex',
        justifyContent: 'center',
        margin: theme.spacing(2, 0),
    },
    count: {
        textAlign: 'left',
        marginLeft: theme.spacing(1),
    },
}))

interface ChargerSessionsDialogProps {
    open: boolean
    charger: Charger
    onClose: () => void
}

const ChargerSessionsDialog: SFC<ChargerSessionsDialogProps> = ({ open, charger, onClose }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const [selectedConnectors, setSelectedConnectors] = useState<Option[]>([])
    const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState<Option[]>([])
    const [sessions, setSessions] = useState<ChargingSession[]>([])
    const [totalCount, setTotalCount] = useState<number>(0)
    const [page, setPage] = useState<number>(0)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<boolean>(false)
    const requestIdRef = useRef(0)

    const buildUrl = useCallback(
        (pageIndex: number) => {
            let url = `chargingSession/history?ChargerId=${charger.id}&`
            selectedConnectors.forEach((o) => (url += `ChargingConnectorIds=${o.id}&`))
            selectedPaymentStatuses.forEach((o) => (url += `PaymentStatusTypes=${o.id}&`))
            url += `OnlyWithInvoice=false&Sort=startedAt desc&page=${pageIndex}&pageSize=${PAGE_SIZE}`
            return url
        },
        [charger.id, selectedConnectors, selectedPaymentStatuses]
    )

    const load = useCallback(
        (pageIndex: number) => {
            const requestId = ++requestIdRef.current
            setLoading(true)
            setError(false)
            getJson(buildUrl(pageIndex))
                .then((result: any) => {
                    if (requestId !== requestIdRef.current) return
                    const items: ChargingSession[] = result?.items ?? []
                    setSessions((prev) => (pageIndex === 1 ? items : prev.concat(items)))
                    setTotalCount(result?.count ?? 0)
                    setPage(pageIndex)
                })
                .catch(() => {
                    if (requestId !== requestIdRef.current) return
                    setError(true)
                })
                .finally(() => {
                    if (requestId === requestIdRef.current) setLoading(false)
                })
        },
        [buildUrl]
    )

    useEffect(() => {
        if (open) {
            setSessions([])
            load(1)
        }
    }, [open, load])

    const connectorOptions: Option[] = [...(charger.connectors ?? [])]
        .sort((a, b) => a.externalId - b.externalId)
        .map((c) => ({
            id: c.id,
            label: `${c.externalId} (${getLabelForChargerConnectorSocketType(c.socketType, t)} ${getLabelForChargerConnectorType(c.type, t)})`,
        }))
    const paymentStatusOptions: Option[] = [
        { id: PaymentStatus.FREE, label: 'FREE' },
        { id: PaymentStatus.PAID, label: 'PAID' },
        { id: PaymentStatus.UNPAID, label: 'UNPAID' },
        { id: PaymentStatus.ROAMING, label: 'ROAMING' },
    ]

    const hasMore = sessions.length < totalCount

    const rows: Array<{ date: string | null; session: ChargingSession | null }> = []
    let lastDay: moment.Moment | null = null
    sessions.forEach((s) => {
        const day = moment(s.startedAt).startOf('day')
        if (!lastDay || !day.isSame(lastDay)) {
            rows.push({ date: day.format('LL'), session: null })
            lastDay = day
        }
        rows.push({ date: null, session: s })
    })

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {t('locations.chargingHistoryTitle', { name: charger.name })}
            </DialogTitle>
            <DialogContent className={classes.content}>
                <div className={classes.filters}>
                    <Autocomplete
                        multiple
                        className={classes.filter}
                        options={connectorOptions}
                        value={selectedConnectors}
                        getOptionLabel={(o: Option) => o.label}
                        getOptionSelected={(o: Option, v: Option) => o.id === v.id}
                        onChange={(e: any, value: Option[]) => setSelectedConnectors(value)}
                        renderInput={(params) => (
                            <TextField {...params} variant="filled" label={t('history.connector')} />
                        )}
                    />
                    <Autocomplete
                        multiple
                        className={classes.filter}
                        options={paymentStatusOptions}
                        value={selectedPaymentStatuses}
                        getOptionLabel={(o: Option) => o.label}
                        getOptionSelected={(o: Option, v: Option) => o.id === v.id}
                        onChange={(e: any, value: Option[]) => setSelectedPaymentStatuses(value)}
                        renderInput={(params) => (
                            <TextField {...params} variant="filled" label={t('history.paymentStatus')} />
                        )}
                    />
                </div>

                {page > 0 && !error && (
                    <Typography variant="caption" component="div" className={classes.count}>
                        {t('locations.chargingHistoryCount', { shown: sessions.length, total: totalCount })}
                    </Typography>
                )}

                {rows.map((row, index) =>
                    row.date ? (
                        <Typography key={`d-${row.date}-${index}`} variant="subtitle2" className={classes.dayTitle}>
                            {row.date}
                        </Typography>
                    ) : (
                        <SessionCard key={row.session!.id} session={row.session!} variant="charger" />
                    )
                )}

                {error && <NoData text={t('locations.chargingHistoryError')} />}
                {!loading && !error && page > 0 && sessions.length === 0 && (
                    <NoData text={t('users.noSessions')} />
                )}

                <div className={classes.footer}>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        hasMore &&
                        !error && (
                            <Button variant="contained" color="primary" onClick={() => load(page + 1)}>
                                {t('common.loadMore')}
                            </Button>
                        )
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    {t('common.close')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ChargerSessionsDialog
